"use client";

import React, { useState, useId, useMemo } from "react";
import styled from "styled-components";
import { Button, Checkbox, Input, Spinner, Text } from "@lmn/design-system";
import IconSearch from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSearch.js";
import IconTrash from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTrash.js";

const Container = styled.div({
  width: "100%",
  minWidth: "320px",
});

const StickySearchHeader = styled.div(({ theme }) => ({
  position: "sticky",
  top: theme.kallisto.space.spaceNegative200,
  display: "flex",
  alignItems: "stretch",
  gap: theme.kallisto.space.space100,
  marginTop: theme.kallisto.space.spaceNegative200,
  marginLeft: theme.kallisto.space.spaceNegative200,
  marginRight: theme.kallisto.space.spaceNegative200,
  padding: theme.kallisto.space.space100,
  background: theme.kallisto.color.background.static.backgroundPage,
  borderBottom: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
  zIndex: 1,
}));

const SearchInputWrapper = styled.div({
  flex: 1,
  minWidth: 0,
});

const DeselectAllButton = styled(Button)`
  &&& {
    align-self: stretch;
    min-width: auto;
    flex-shrink: 0;
  }
`;

const OptionsList = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.kallisto.space.space100,
  marginTop: theme.kallisto.space.space200,
}));

const EmptyState = styled.div(({ theme }) => ({
  marginTop: theme.kallisto.space.space200,
  padding: `${theme.kallisto.space.space300} 0`,
  textAlign: "center",
}));

const LoadingWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.kallisto.space.space300} 0`,
}));

const VisuallyHidden = styled.span({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
});

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 0;
`;

export interface SearchableCheckboxListOption {
  value: string;
  label: string;
}

export interface SearchableCheckboxListProps {
  options: SearchableCheckboxListOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * When true, suppresses the checkbox's native onChange and uses an absolute
   * ClickOverlay to capture clicks. Required inside filter dialogs that defer
   * commits until the user confirms.
   */
  delayedUpdate?: boolean;
  searchLabel: string;
  searchPlaceholder?: string;
  listAriaLabel: string;
  emptyStateText: string;
  /** Called to format the screen-reader live-region announcement on search. */
  formatResultCount: (filtered: number, total: number) => string;
  deselectAllLabel?: string;
  testIdPrefix?: string;
  loading?: boolean;
}

const SearchableCheckboxList: React.FC<SearchableCheckboxListProps> = ({
  options,
  value,
  onChange,
  delayedUpdate = false,
  searchLabel,
  searchPlaceholder = "Suchen...",
  listAriaLabel,
  emptyStateText,
  formatResultCount,
  deselectAllLabel = "Auswahl entfernen",
  testIdPrefix = "searchableCheckboxList",
  loading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const listId = useId();
  const liveRegionId = useId();

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );
  };

  const handleDeselectAll = () => onChange([]);

  if (loading) {
    return (
      <Container data-testid={testIdPrefix} aria-busy>
        <LoadingWrapper data-testid={`${testIdPrefix}-loading`}>
          <Spinner size={24} />
        </LoadingWrapper>
      </Container>
    );
  }

  return (
    <Container data-testid={testIdPrefix}>
      <StickySearchHeader>
        <SearchInputWrapper>
          <Input
            label={searchLabel}
            placeholder={searchPlaceholder}
            type="text"
            isFullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={<IconSearch size={20} aria-hidden="true" />}
            input={{
              "aria-controls": listId,
              "aria-describedby": liveRegionId,
              autoComplete: "off",
              inputMode: "search",
            }}
          />
        </SearchInputWrapper>
        {value.length > 0 && (
          <DeselectAllButton
            variant="primary"
            appearance="ghost"
            onClick={handleDeselectAll}
            iconLeading={IconTrash}
            iconSize={20}
            aria-label={deselectAllLabel}
            title={deselectAllLabel}
            data-testid={`${testIdPrefix}-deselect-all`}
          />
        )}
      </StickySearchHeader>
      <VisuallyHidden id={liveRegionId} aria-live="polite" aria-atomic="true">
        {searchQuery.trim()
          ? formatResultCount(filteredOptions.length, options.length)
          : ""}
      </VisuallyHidden>
      {filteredOptions.length === 0 ? (
        <EmptyState>
          <Text variant="BodySmall" color="textSecondary">
            {emptyStateText}
          </Text>
        </EmptyState>
      ) : (
        <OptionsList id={listId} role="group" aria-label={listAriaLabel}>
          {filteredOptions.map((option) => {
            const isChecked = value.includes(option.value);
            return (
              <CheckboxWrapper key={option.value}>
                {delayedUpdate && (
                  <ClickOverlay onClick={() => toggle(option.value)} />
                )}
                <Checkbox
                  checkbox={{
                    id: `${testIdPrefix}-${option.value}`,
                    label: option.label,
                  }}
                  isChecked={isChecked}
                  onChange={
                    delayedUpdate ? () => { } : () => toggle(option.value)
                  }
                />
              </CheckboxWrapper>
            );
          })}
        </OptionsList>
      )}
    </Container>
  );
};

export default SearchableCheckboxList;
